import { useState, useMemo, useRef, useEffect } from 'react';
import { message } from 'antd';
import { computePortfolioSummary } from '../utils';
import { smartDownload } from '../../../utils/downloadToWorkspace';

export const useBacktestResults = (callAction, runInfo, runMeta, setRunInfo) => {
  const [resultsOpen, setResultsOpen] = useState(false);
  const [resultsData, setResultsData] = useState(null);
  const [eqCheck, setEqCheck] = useState(null);
  const [eqCheckOpen, setEqCheckOpen] = useState(false);
  const [twrSummary, setTwrSummary] = useState(null);
  const [warningsData, setWarningsData] = useState(null);
  const [universeOpen, setUniverseOpen] = useState(false);
  const [universeJson, setUniverseJson] = useState(null);
  const [universeList, setUniverseList] = useState([]);
  
  // DAS-1681: Results loading progress state
  const [resultsLoadingInfo, setResultsLoadingInfo] = useState({
    isLoading: false,
    stage: '',
    startTime: null,
    elapsedSeconds: 0
  });
  const resultsLoadingTimerRef = useRef(null);
  
  // DAS-1681: Update elapsed seconds while loading
  useEffect(() => {
    if (resultsLoadingInfo.isLoading && resultsLoadingInfo.startTime) {
      resultsLoadingTimerRef.current = setInterval(() => {
        setResultsLoadingInfo(prev => ({
          ...prev,
          elapsedSeconds: Math.floor((Date.now() - prev.startTime) / 1000)
        }));
      }, 1000);
    } else if (resultsLoadingTimerRef.current) {
      clearInterval(resultsLoadingTimerRef.current);
      resultsLoadingTimerRef.current = null;
    }
    return () => {
      if (resultsLoadingTimerRef.current) {
        clearInterval(resultsLoadingTimerRef.current);
      }
    };
  }, [resultsLoadingInfo.isLoading, resultsLoadingInfo.startTime]);
  
  // DAS-1479: Price/FX Debug state
  const [pricesDebugData, setPricesDebugData] = useState(null);
  const [pricesDebugLoading, setPricesDebugLoading] = useState(false);
  const [selectedDebugDate, setSelectedDebugDate] = useState(null);

  // DAS-1592: Trade Clause Timeline state
  const [tradeTimelineModal, setTradeTimelineModal] = useState({
    open: false,
    loading: false,
    data: null,
    event: null,
  });

  // Alert Details Modal state
  const [alertDetailModal, setAlertDetailModal] = useState({
    open: false,
    alertId: null,
    loading: false,
    summary: null,
    fields: null,
  });

  // Clause Details Modal state
  const [clauseDetailModal, setClauseDetailModal] = useState({
    open: false,
    meta: null,
    event: null,
  });

  // DAS-1662: Rebalancing Selection Modal state
  const [rebalancingModal, setRebalancingModal] = useState({
    open: false,
    runId: null,
    ticker: null,
    rebalancingDate: null,
  });

  const saveResultsToCache = (runId, data, checkData = null) => {
    try {
      const cacheKey = `backtest_results_${runId}`;
      const cacheData = {
        timestamp: Date.now(),
        results: data,
        eqCheck: checkData
      };
      sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (e) {
      console.warn('Failed to cache results', e);
    }
  };

  const loadResultsFromCache = (runId) => {
    try {
      const cacheKey = `backtest_results_${runId}`;
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        return JSON.parse(raw);
      }
    } catch {
      // ignore
    }
    return null;
  };

  const applyResultsData = (data, eqCheckData = null, runInfoRef = null) => {
    setResultsData(data);
    setResultsOpen(true);
    
    // DAS-1653: Update runInfo with config from results if available
    if (runInfoRef && data?.config) {
      runInfoRef.setRunInfo(prev => ({
        ...prev,
        config: data.config
      }));
    }
    
    // DAS-1671: Backend returns 'twr', not 'twr_summary'
    if (data?.twr) setTwrSummary(data.twr);
    else setTwrSummary(null);

    if (data?.warnings) setWarningsData(data.warnings);
    else setWarningsData(null);

    if (data?.universe_debug) {
      setUniverseJson(JSON.stringify(data.universe_debug, null, 2));
      if (Array.isArray(data.universe_debug)) {
        setUniverseList(data.universe_debug);
      } else if (data.universe_debug.universe && Array.isArray(data.universe_debug.universe)) {
        setUniverseList(data.universe_debug.universe);
      }
    } else {
      setUniverseJson(null);
      setUniverseList([]);
    }

    if (eqCheckData) setEqCheck(eqCheckData);
  };

  // DAS-1748: Add forceRefresh parameter to bypass cache
  const onResults = async (runId = null, forceRefresh = false) => {
    const rId = runId || runInfo?.run_id;
    if (!rId) return message.info('No run ID');
    
    // DAS-1748: Skip cache if forceRefresh is true, or if cache is older than 5 minutes
    const cached = loadResultsFromCache(rId);
    const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
    const isCacheValid = cached && cached.timestamp && (Date.now() - cached.timestamp < CACHE_TTL_MS);
    
    if (!forceRefresh && isCacheValid) {
      applyResultsData(cached.results, cached.eqCheck, { setRunInfo });
      return;
    }

    // DAS-1681/DAS-1690: Start tracking loading progress with descriptive stages
    setResultsLoadingInfo({
      isLoading: true,
      stage: 'Fetching positions & trades...',
      startTime: Date.now(),
      elapsedSeconds: 0
    });

    try {
      // DAS-1776: Force refresh to bypass cache and get latest data including currency_summary
      const res = await callAction('get_run_results', { run_id: rId, force_refresh: true }, { useLoader: false });
      if (res?.success) {
        // DAS-1690: Update stage - building charts
        setResultsLoadingInfo(prev => ({ ...prev, stage: 'Building equity curve & charts...' }));
        
        applyResultsData(res.data, null, { setRunInfo });
        
        // DAS-1690: Update stage for equity check
        setResultsLoadingInfo(prev => ({ ...prev, stage: 'Verifying calculations...' }));
        
        const eqRes = await callAction('equity_check_no_events', { run_id: rId }, { useLoader: false });
        if (eqRes?.success) {
          setEqCheck(eqRes.data);
          saveResultsToCache(rId, res.data, eqRes.data);
        } else {
          saveResultsToCache(rId, res.data, null);
        }
      } else {
        message.error('Failed to load results');
      }
    } finally {
      // DAS-1681: Stop loading
      setResultsLoadingInfo({
        isLoading: false,
        stage: '',
        startTime: null,
        elapsedSeconds: 0
      });
    }
  };

  // DAS-1748: Force refresh equity check (bypasses cache)
  const refreshEquityCheck = async (runId = null) => {
    // Extract run_id as plain number to avoid circular references
    const rId = Number(runId || runInfo?.run_id);
    if (!rId || isNaN(rId)) return message.info('No run ID');
    
    message.loading({ content: 'Refreshing equity check...', key: 'eq_refresh' });
    
    try {
      // Clear cache for this run
      try {
        const cacheKey = `backtest_results_${rId}`;
        sessionStorage.removeItem(cacheKey);
      } catch (e) {
        // ignore cache clear errors
      }
      
      // DAS-1748: Pass only primitive values to avoid circular JSON errors
      const eqRes = await callAction('equity_check_no_events', { run_id: rId }, { useLoader: false });
      if (eqRes?.success) {
        setEqCheck(eqRes.data);
        message.success({ content: 'Equity check refreshed!', key: 'eq_refresh' });
        
        // Update cache with new equity check data
        if (resultsData) {
          saveResultsToCache(rId, resultsData, eqRes.data);
        }
      } else {
        message.error({ content: 'Failed to refresh equity check', key: 'eq_refresh' });
      }
    } catch (e) {
      console.error('refreshEquityCheck error:', e);
      message.error({ content: 'Failed to refresh equity check', key: 'eq_refresh' });
    }
  };

  // Chart data memoization
  const chartData = useMemo(() => {
    const eq = resultsData?.equity_curve || [];
    const bm = resultsData?.benchmark_curve || [];  // Primary benchmark (Buy-and-Hold)
    const sbm = resultsData?.secondary_benchmark_curve || [];  // DAS-1659: Secondary benchmark
    const ev = Array.isArray(resultsData?.events_list) ? resultsData.events_list : (Array.isArray(resultsData?.events) ? resultsData.events : []);

    const formatDate = (raw) => {
      if (!raw) return '';
      if (raw instanceof Date) return raw.toISOString().split('T')[0];
      if (typeof raw === 'string') {
        // If already in YYYY-MM-DD format, return as-is
        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
        // Otherwise parse as date (handles "Sun, 01 Jan 2023 00:00:00 GMT" format)
        try {
          const d = new Date(raw);
          return d.toISOString().split('T')[0];
        } catch {
          return raw.substring(0, 10); // Fallback
        }
      }
      return '';
    };

    const cdDates = eq.map(e => formatDate(e.pos_date));
    // DAS-1743: Use pre-extracted cash_eur field from backend (more reliable)
    // The backend already parses cash_by_currency_json and extracts EUR value
    // Falls back to parsing cash_by_currency_json if cash_eur not available
    const cdVals = eq.map(e => {
      // First try the pre-extracted cash_eur field
      if (e.cash_eur !== undefined && e.cash_eur !== null) {
        return Number(e.cash_eur) || 0;
      }
      // Fallback to parsing cash_by_currency_json
      try {
        const map = typeof e.cash_by_currency_json === 'string' ? JSON.parse(e.cash_by_currency_json) : e.cash_by_currency_json;
        return map?.EUR || 0;
      } catch { return 0; }
    });

    const odDates = cdDates;
    const odEquity = eq.map(e => Number(e.equity_base_eur || 0));
    
    const bmMap = {};
    bm.forEach(b => { bmMap[formatDate(b.date)] = Number(b.index_norm || b.value || 0); });
    
    const startEquity = odEquity[0] || 10000;
    const startBench = bmMap[odDates[0]] || 100;
    const bmFactor = startBench ? startEquity / startBench : 0;
    
    const odBench = odDates.map(d => (bmMap[d] || 0) * bmFactor);
    
    // For Performance Comparison chart, we need UNSCALED benchmark (raw index values)
    const odBenchRaw = odDates.map(d => bmMap[d] || 0);

    // DAS-1659: Process secondary benchmark data (if available)
    // FIX: Forward-fill for weekends/holidays instead of using 0 (which breaks chart scaling)
    const sbmMap = {};
    sbm.forEach(b => { sbmMap[formatDate(b.date)] = Number(b.index_norm || b.value || 0); });
    const startSecondaryBench = sbmMap[odDates[0]] || sbmMap[odDates[1]] || sbmMap[odDates[2]] || 100;
    const sbmFactor = startSecondaryBench ? startEquity / startSecondaryBench : 0;
    
    // Forward-fill secondary benchmark for days without data (weekends/holidays)
    let lastSbmValue = null;
    const odSecondaryBench = odDates.map(d => {
      const val = sbmMap[d];
      if (val != null && val > 0) {
        lastSbmValue = val * sbmFactor;
        return lastSbmValue;
      }
      // Use last known value for weekends/holidays, or null if no data yet
      return lastSbmValue;
    });

    const exVals = odDates.map((d, i) => {
      const eqVal = odEquity[i];
      const bmVal = odBench[i];
      if (!startEquity) return 0;
      return ((eqVal - bmVal) / startEquity) * 100;
    });

    const txs = ev.filter(e => {
      const type = String(e.event_type || '').trim().toLowerCase();
      return type === 'trade';
    }).map(e => ({
      date: formatDate(e.event_date),
      type: (e.qty > 0 ? 'BUY' : 'SELL'),
      ticker: e.ticker,
      qty: e.qty,
      price: e.price,
      fees: e.fees,
      cashEffect: -(e.qty * e.price + e.fees),
      index: odDates.indexOf(formatDate(e.event_date)),
      value: 0
    }));


    const cashTxs = txs.map(t => ({ ...t, value: cdVals[t.index] || 0 })).filter(t => t.index !== -1);
    const excessTxs = txs.map(t => {
      // Find closest index if exact match fails (handle weekends/holidays mismatch)
      let idx = t.index;
      const originalIdx = idx;
      if (idx === -1 && odDates.length > 0) {
        // Find closest date
        const tDate = new Date(t.date).getTime();
        let minDiff = Infinity;
        let closestIdx = -1;
        odDates.forEach((d, i) => {
          const diff = Math.abs(new Date(d).getTime() - tDate);
          if (diff < minDiff) {
            minDiff = diff;
            closestIdx = i;
          }
        });
        // Only snap if within 5 days (to avoid matching far off dates)
        if (minDiff < 5 * 24 * 60 * 60 * 1000) {
          idx = closestIdx;
        }
      }
      return { ...t, value: exVals[idx] || 0, index: idx };
    }).filter(t => t.index !== -1);
    

    const lastExcess = exVals[exVals.length - 1];
    const exColor = (!exVals || exVals.length === 0) ? '#52c41a' : (lastExcess >= 0 ? '#52c41a' : '#cf1322');

    // Simple Performance Metrics (End - Start) / Start
    const startEq = odEquity[0] || 0;
    const endEq = odEquity[odEquity.length - 1] || 0;
    const strategyReturn = startEq ? (endEq - startEq) / startEq : 0;

    const startBm = odBench[0] || 0;
    const endBm = odBench[odBench.length - 1] || 0;
    const benchmarkReturn = startBm ? (endBm - startBm) / startBm : 0;

    const excessReturn = strategyReturn - benchmarkReturn;

    const performanceMetrics = {
      strategyReturn,
      benchmarkReturn,
      excessReturn,
      startEquity: startEq,
      endEquity: endEq,
      startBenchmark: startBm,
      endBenchmark: endBm
    };

    return {
      equityCurve: eq,
      benchmarkCurve: bm,
      eventsList: ev,
      cashData: { dates: cdDates, vals: cdVals },
      overlayData: { dates: odDates, equity: odEquity, bench: odBench },
      excessData: { dates: odDates, vals: exVals },
      excessTransactions: excessTxs,
      cashTransactions: cashTxs,
      excessLineColor: exColor,
      performanceMetrics, // Expose this
      // For Performance Comparison chart - BOTH must be in same units (EUR)
      // Use odBench (scaled benchmark) NOT odBenchRaw
      equityData: { dates: odDates, values: odEquity },
      benchmarkData: { dates: odDates, values: odBench }, // FIX: Use scaled benchmark
      secondaryBenchmarkData: { dates: odDates, values: odSecondaryBench }, // DAS-1659: Secondary benchmark
    };
  }, [resultsData]);

  const portfolioSummary = useMemo(() => 
    computePortfolioSummary(chartData.equityCurve, chartData.eventsList), 
  [chartData.equityCurve, chartData.eventsList]);

  const loadPricesDebugData = async () => {
    if (!runInfo?.run_id) {
      message.info('No run yet to load debug data for.');
      return;
    }
    setPricesDebugLoading(true);
    try {
      const data = await callAction('get_run_prices_debug', { run_id: runInfo.run_id });
      if (data?.success && data.data) {
        setPricesDebugData(data.data);
        if (data.data.dates && data.data.dates.length > 0) {
          setSelectedDebugDate(data.data.dates[0]);
        }
        message.success('Price/FX debug data loaded.');
      } else {
        message.error('Failed to fetch price/FX debug data.');
        setPricesDebugData(null);
      }
    } catch (error) {
      console.error('Error loading price/FX debug data:', error);
      message.error('Error loading price/FX debug data.');
      setPricesDebugData(null);
    } finally {
      setPricesDebugLoading(false);
    }
  };

  const selectedDateDebugData = useMemo(() => {
    if (!pricesDebugData || !selectedDebugDate) return null;
    const dateIndex = pricesDebugData.dates.indexOf(selectedDebugDate);
    if (dateIndex === -1) return null;

    const dateData = {
      date: selectedDebugDate,
      equity_eur: pricesDebugData.equity_eur[dateIndex],
      cash_by_currency: pricesDebugData.cash_by_currency[dateIndex],
      positions: [],
    };

    for (const ticker of pricesDebugData.tickers) {
      const tickerData = pricesDebugData.ticker_data[ticker];
      if (tickerData) {
        const qty = tickerData.qty[dateIndex];
        if (qty > 0) {
          dateData.positions.push({
            ticker: ticker,
            qty: qty,
            currency: tickerData.currency,
            local_price: tickerData.local_price[dateIndex],
            fx_rate: tickerData.fx_rate[dateIndex],
            eur_price: tickerData.eur_price[dateIndex],
            position_value_eur: tickerData.position_value_eur[dateIndex],
            price_source_column: tickerData.price_source_column[dateIndex],
          });
        }
      }
    }
    return dateData;
  }, [pricesDebugData, selectedDebugDate]);

  // Trade Timeline logic
  const openTradeTimeline = async (event) => {
    setTradeTimelineModal({
      open: true,
      loading: true,
      data: null,
      event,
    });

    try {
      const reasonMeta = event.reason_meta;
      if (!reasonMeta || !reasonMeta.clause_details || !reasonMeta.clause_details.length) {
        message.warning('No clause details available for this trade');
        setTradeTimelineModal({ open: false, loading: false, data: null, event: null });
        return;
      }

      const hit = reasonMeta.clause_details[0];
      const payload = {
        ticker: event.ticker,
        trade_date: event.event_date,
        field: hit.field,
        threshold_value: hit.threshold_source === 'literal' ? hit.threshold_value : null,
        threshold_field: hit.threshold_source === 'dynamic' ? hit.threshold_field : null,
        threshold_sourcetable: hit.threshold_source === 'dynamic' ? hit.threshold_sourcetable : null,
        months_before: 2,
        months_after: 2,
      };

      const data = await callAction('get_trade_clause_timeline', payload);
      if (data?.success) {
        setTradeTimelineModal({
          open: true,
          loading: false,
          data: data.data,
          event,
        });
      } else {
        message.error('Failed to fetch timeline data: ' + (data?.error || 'Unknown error'));
        setTradeTimelineModal({ open: false, loading: false, data: null, event: null });
      }
    } catch (e) {
      message.error('Error fetching timeline: ' + e.message);
      setTradeTimelineModal({ open: false, loading: false, data: null, event: null });
    }
  };

  const closeTradeTimeline = () => {
    setTradeTimelineModal({
      open: false,
      loading: false,
      data: null,
      event: null,
    });
  };

  const copyTradeTimelineJson = () => {
    const { data, event } = tradeTimelineModal;
    if (!data || !event) return;
    // ... (simplified logic for brevity if unchanged)
    const reasonMeta = event.reason_meta;
    const jsonData = {
      run_id: runInfo?.run_id,
      ticker: data.ticker,
      // ... (rest of properties)
      trade_date: data.trade_date,
      timeline: { ...data } // simplified for brevity
    };
    navigator.clipboard.writeText(JSON.stringify(jsonData, null, 2));
    message.success('Timeline data copied to clipboard');
  };

  const createTradeTimelineJiraTicket = async () => {
    message.info('Not implemented yet');
  };

  // Alert Details logic
  const openAlertDetails = async (alertId) => {
    if (!alertId) return;
    setAlertDetailModal({
      open: true,
      alertId,
      loading: true,
      summary: null,
      fields: null,
    });
    try {
      const [fieldsRes, summaryRes] = await Promise.all([
        callAction('alert_definition_fields', { id: alertId }, { useLoader: false }),
        callAction('list_alert_definitions', { q: alertId, page: 1, page_size: 1 }, { useLoader: false }),
      ]);
      const summaryItem = (summaryRes?.data?.items || []).find((item) => Number(item.ID) === Number(alertId)) || null;
      setAlertDetailModal((prev) => ({
        ...prev,
        loading: false,
        summary: summaryItem,
        fields: fieldsRes?.data || null,
      }));
    } catch (e) {
      message.error('Failed to load alert details');
      setAlertDetailModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const closeAlertDetails = () => {
    setAlertDetailModal({
      open: false,
      alertId: null,
      loading: false,
      summary: null,
      fields: null,
    });
  };

  // Clause Details logic
  const openClauseDetails = (meta, eventRow) => {
    if (!meta) return;
    setClauseDetailModal({
      open: true,
      meta,
      event: eventRow,
    });
  };

  const closeClauseDetails = () => {
    setClauseDetailModal({
      open: false,
      meta: null,
      event: null,
    });
  };

  // DAS-1662: Rebalancing Selection Modal handlers
  const openRebalancingDetails = (event) => {
    setRebalancingModal({
      open: true,
      runId: runInfo?.run_id,
      ticker: event.ticker,
      rebalancingDate: event.event_date,
    });
  };

  const closeRebalancingDetails = () => {
    setRebalancingModal({
      open: false,
      runId: null,
      ticker: null,
      rebalancingDate: null,
    });
  };

  const copyResultsJson = async () => {
    try {
      if (!runInfo?.run_id) return message.info('No run yet');
      await navigator.clipboard.writeText(JSON.stringify(resultsData, null, 2));
      message.success('Results JSON copied');
    } catch {
      message.error('Failed to copy results');
    }
  };

  const downloadResultsJson = async () => {
    if (!runInfo?.run_id) return message.info('No run yet');
    await smartDownload(
      callAction,
      resultsData,
      `run_${runInfo.run_id}_results`,
      { type: 'json', addTimestamp: true, showMessage: message }
    );
  };

  const downloadPricesAuditJson = async () => {
    if (!runInfo?.run_id) return message.info('No run yet');
    try {
      const res = await callAction('get_run_prices_debug', { run_id: runInfo.run_id });
      if (res?.success) {
        await smartDownload(
          callAction,
          res.data,
          `run_${runInfo.run_id}_prices_audit`,
          { type: 'json', addTimestamp: true, showMessage: message }
        );
      } else {
        message.error('Failed to fetch audit data');
      }
    } catch {
      message.error('Failed to fetch audit data');
    }
  };

  const copyRunConfigJson = async () => {
    const configJson = runMeta?.configuration_json || (runInfo?.config ? JSON.stringify(runInfo.config, null, 2) : null);
    if (!configJson) return message.info('No config yet');
    try {
      await navigator.clipboard.writeText(configJson);
      message.success('Configuration JSON copied');
    } catch {
      message.error('Failed to copy config');
    }
  };

  const downloadRunConfigJson = async () => {
    const configJson = runMeta?.configuration_json || (runInfo?.config ? JSON.stringify(runInfo.config, null, 2) : null);
    if (!configJson) return message.info('No config yet');
    
    // Parse if it's a string, otherwise use as-is
    const configData = typeof configJson === 'string' ? JSON.parse(configJson) : configJson;
    
    await smartDownload(
      callAction,
      configData,
      `run_${runInfo.run_id}_config`,
      { type: 'json', addTimestamp: true, showMessage: message }
    );
  };

  const exportResultsCsv = () => {
    if (!chartData?.overlayData?.dates || chartData.overlayData.dates.length === 0) {
      return message.info('No results to export');
    }
    
    let csv = 'Date,Equity (EUR),Benchmark\n';
    chartData.overlayData.dates.forEach((d, i) => {
      const eq = chartData.overlayData.equity[i] || 0;
      const bm = chartData.overlayData.bench[i] || 0;
      csv += `${d},${eq.toFixed(2)},${bm.toFixed(2)}\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `run_${runInfo?.run_id || 'results'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const createRunJiraTicket = async () => {
    if (!runInfo?.run_id) return message.info('No run yet');
    
    const note = window.prompt('Optional: Enter a note for the ticket (e.g., "Testing moving average strategy")');
    if (note === null) return; // Cancelled
    
    try {
      const res = await callAction('create_run_jira_ticket', { 
        run_id: runInfo.run_id,
        note: note || ''
      });
      
      if (res?.success && res.data?.key) {
        message.success(`Created ticket ${res.data.key}`);
      } else if (res?.error) {
        message.error(`Failed: ${res.error}`);
      } else {
        message.error('Failed to create ticket');
      }
    } catch (e) {
      message.error('Error creating ticket');
    }
  };

  return {
    resultsOpen,
    setResultsOpen,
    resultsData,
    setResultsData,
    eqCheck,
    setEqCheck,
    eqCheckOpen,
    setEqCheckOpen,
    twrSummary,
    warningsData,
    universeOpen,
    setUniverseOpen,
    universeJson,
    universeList,
    pricesDebugData,
    pricesDebugLoading,
    selectedDebugDate,
    setSelectedDebugDate,
    chartData,
    portfolioSummary,
    selectedDateDebugData,
    onResults,
    refreshEquityCheck,  // DAS-1748: Force refresh equity check
    applyResultsData,
    loadPricesDebugData,
    copyResultsJson,
    downloadResultsJson,
    downloadPricesAuditJson,
    copyRunConfigJson,
    downloadRunConfigJson,
    exportResultsCsv,
    createRunJiraTicket,
    // DAS-1681: Results loading progress
    resultsLoadingInfo,
    // Trade Timeline
    tradeTimelineModal,
    openTradeTimeline,
    closeTradeTimeline,
    copyTradeTimelineJson,
    createTradeTimelineJiraTicket,
    // Alert Details
    alertDetailModal,
    openAlertDetails,
    closeAlertDetails,
    // Clause Details
    clauseDetailModal,
    openClauseDetails,
    closeClauseDetails,
    // DAS-1662: Rebalancing Selection Details
    rebalancingModal,
    openRebalancingDetails,
    closeRebalancingDetails,
  };
};
